import React, { useState, useEffect } from 'react';
import { TournamentCreateInput } from '../../services/tournamentsService';
import { organizationsService } from '../../services';
import SearchableDropdown from '../shared/SearchableDropdown';

interface AddTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: TournamentCreateInput) => Promise<void>;
}

const AddTournamentModal: React.FC<AddTournamentModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  
  const [formData, setFormData] = useState<TournamentCreateInput>({
    name: '',
    logo: null,
    country: '',
    active: true,
    organisation_id: null, // No auto-selection
    data_source: 'official'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [organizations, setOrganizations] = useState<Array<{id: string, name: string}>>([]);

  // Fetch organizations when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen]);

  const fetchOrganizations = async () => {
    try {
      const orgs = await organizationsService.getAll();
      setOrganizations(orgs.map(org => ({ id: org.id, name: org.name })));
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleInputChange = (field: keyof TournamentCreateInput, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Tournament name is required';
    }

    if (!formData.country?.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.data_source?.trim()) {
      newErrors.data_source = 'Data source is required';
    }

    // Validate logo URL if provided
    if (formData.logo && formData.logo.trim()) {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.logo)) {
        newErrors.logo = 'Please enter a valid URL starting with http:// or https://';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
      // Reset form
      setFormData({
        name: '',
        logo: null,
        country: '',
        active: true,
        organisation_id: null,
        data_source: 'official'
      });
      setErrors({});
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error creating tournament:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add New Tournament</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tournament Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tournament Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter tournament name"
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          {/* Logo URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Logo URL
            </label>
            <input
              type="url"
              value={formData.logo || ''}
              onChange={(e) => handleInputChange('logo', e.target.value || null)}
              className={`w-full px-3 py-2 border ${errors.logo ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter logo URL (optional)"
            />
            {errors.logo && (
              <p className="text-red-500 text-xs mt-1">{errors.logo}</p>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className={`w-full px-3 py-2 border ${errors.country ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter country"
            />
            {errors.country && (
              <p className="text-red-500 text-xs mt-1">{errors.country}</p>
            )}
          </div>

          {/* Data Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Source *
            </label>
            <select
              value={formData.data_source}
              onChange={(e) => handleInputChange('data_source', e.target.value)}
              className={`w-full px-3 py-2 border ${errors.data_source ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="official">Official</option>
              <option value="custom">Custom</option>
            </select>
            {errors.data_source && (
              <p className="text-red-500 text-xs mt-1">{errors.data_source}</p>
            )}
          </div>

          {/* Organization Selection (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization
            </label>
            <SearchableDropdown
              options={organizations}
              value={formData.organisation_id || ''}
              onChange={(value) => handleInputChange('organisation_id', value)}
              placeholder="Search organizations..."
              label=""
            />
            <p className="text-gray-500 text-xs mt-1">Leave empty for global tournament</p>
          </div>

          {/* Active Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Active Status
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleInputChange('active', !formData.active)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formData.active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    formData.active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">
                {formData.active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              {isSaving ? 'Saving...' : 'Add Data'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTournamentModal;
